import { LucideShieldQuestion } from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
} from "../sidebar";
import { Dispatch, ReactNode, SetStateAction, useCallback, useMemo } from "react";
import { ExpandableDialog, ExpandableDialogProps } from "../dialogs";
import { useUncontrolledState } from "../../hooks";
import { getChildProps, getNamedChildren } from "../../utils";
import {
  EXPANDABLE_DIALOG_WITH_SIDEBAR_SECTION_NAME,
  EXPANDABLE_DIALOG_WITH_SIDEBAR_SUBSECTION_NAME,
} from "./atoms/constants";
import { Section, SectionProps } from "./atoms/Section";
import { Subsection, SubsectionProps } from "./atoms/Subsection";
import { SectionHeader, type SectionHeaderProps } from "./atoms/SectionHeader";
import { DialogTitle } from "../dialog";

export interface ExpandableDialogWithSidebarProps<Section extends string = string>
  extends Omit<ExpandableDialogProps, "showDefaultExpandButton"> {
  title: string;
  selectedSection?: Section;
  setSelectedSection?: Dispatch<SetStateAction<Section>>;
  headerActions?: SectionHeaderProps["headerActions"];
  hideExpandToggle?: boolean;
}

interface SectionMetadata {
  component: ReactNode;
  props: SectionProps | SubsectionProps;
  sectionKey: string;
  subsections?: SectionMetadata[];
  directContent?: ReactNode[];
  parentKey?: string;
  depth: number; // Track nesting depth for UI
  isCustomSidebarSection?: boolean; // True if this section has a custom sidebar component
}

/**
 * Expandable dialog with sidebar navigation for organizing content into sections.
 *
 * Automatically generates a sidebar menu from child `Section` components. The sidebar
 * displays only top-level sections with icons and titles. The selected section's content
 * is shown in the main area. Sidebar is hidden on mobile, visible on md+ screens.
 *
 * Supports unlimited nesting of subsections within sections or other subsections.
 * Navigation between nested levels is handled through breadcrumb links that appear
 * in the section header.
 *
 * Each section displays breadcrumb navigation showing the full hierarchy:
 * Dialog Title > Section Title > Subsection Title > ...
 * Clicking on any breadcrumb navigates to that level.
 *
 * @param title - The title of the dialog, displayed in breadcrumbs and for screen readers
 * @param [selectedSection] - Currently selected section key (controlled mode)
 * @param [setSelectedSection] - Callback to update selected section (controlled mode)
 *
 * @example
 * ```tsx
 * <ExpandableDialogWithSidebar open title="Contact Details">
 *   <ExpandableDialogWithSidebar.Section
 *     title="Overview"
 *     icon={HomeIcon}
 *   >
 *     <div>Overview content</div>
 *   </ExpandableDialogWithSidebar.Section>
 *   <ExpandableDialogWithSidebar.Section
 *     title="Settings"
 *     icon={SettingsIcon}
 *   >
 *     <div>Settings content</div>
 *     <ExpandableDialogWithSidebar.Subsection title="Privacy">
 *       <div>Privacy settings content</div>
 *       <ExpandableDialogWithSidebar.Subsection title="Data Collection">
 *         <div>Nested data collection settings</div>
 *       </ExpandableDialogWithSidebar.Subsection>
 *     </ExpandableDialogWithSidebar.Subsection>
 *   </ExpandableDialogWithSidebar.Section>
 *   <ExpandableDialogWithSidebar.Section
 *     title="Help Link"
 *     customSidebarComponent={
 *       <a href="https://help.example.com" target="_blank">
 *         <SidebarMenuButton>
 *           <HelpCircleIcon />
 *           <span>Help</span>
 *         </SidebarMenuButton>
 *       </a>
 *     }
 *   />
 * </ExpandableDialogWithSidebar>
 * ```
 */
const ExpandableDialogWithSidebarComponent = ({
  title,
  selectedSection: providedSelectedSection,
  setSelectedSection: providedSetSelectedSection,
  expanded: providedExpanded,
  setExpanded: providedSetExpanded,
  children,
  headerActions,
  hideExpandToggle,
  modal,
  ...expandableDialogProps
}: ExpandableDialogWithSidebarProps) => {
  const [sectionChildren, otherChildren] = useMemo(
    () => getNamedChildren(children, EXPANDABLE_DIALOG_WITH_SIDEBAR_SECTION_NAME),
    [children]
  );
  const hasSections = !!sectionChildren.length;

  // Recursive function to extract subsections at any depth
  const extractSubsections = useCallback((children: ReactNode, parentKey: string, depth: number): SectionMetadata[] => {
    const [subsectionChildren] = getNamedChildren(children, EXPANDABLE_DIALOG_WITH_SIDEBAR_SUBSECTION_NAME);

    return subsectionChildren.reduce<SectionMetadata[]>((acc, subsection) => {
      const subProps = getChildProps<SubsectionProps>(subsection);
      if (subProps) {
        const subsectionKey = `${parentKey}.${subProps.sectionKey ?? subProps.title}`;

        // Extract direct children from this subsection (non-subsection content)
        const [, subDirectChildren] = getNamedChildren(
          subProps.children,
          EXPANDABLE_DIALOG_WITH_SIDEBAR_SUBSECTION_NAME
        );

        // Recursively extract nested subsections
        const nestedSubsections = extractSubsections(subProps.children, subsectionKey, depth + 1);

        acc.push({
          component: subsection,
          props: subProps,
          sectionKey: subsectionKey,
          parentKey: parentKey,
          depth: depth,
          subsections: nestedSubsections.length > 0 ? nestedSubsections : undefined,
          directContent: subDirectChildren,
        });
      }
      return acc;
    }, []);
  }, []);

  // Build section metadata with nested subsections
  const sections = useMemo(() => {
    return sectionChildren.reduce<SectionMetadata[]>((acc, section) => {
      const props = getChildProps<SectionProps>(section);
      if (props) {
        const sectionKey = props.sectionKey ?? props.title;
        const isCustomSidebarSection = !!props.customSidebarComponent;

        // Extract subsections from this section's children (depth starts at 1)
        const subsections = isCustomSidebarSection ? undefined : extractSubsections(props.children, sectionKey, 1);

        // Extract direct children (non-subsection content)
        const [, directChildren] = isCustomSidebarSection
          ? [[], []]
          : getNamedChildren(props.children, EXPANDABLE_DIALOG_WITH_SIDEBAR_SUBSECTION_NAME);

        acc.push({
          component: section,
          props,
          sectionKey: sectionKey,
          subsections: subsections && subsections.length > 0 ? subsections : undefined,
          directContent: directChildren,
          depth: 0,
          isCustomSidebarSection,
        });
      }
      return acc;
    }, []);
  }, [sectionChildren, extractSubsections]);

  // Recursively flatten all sections and subsections for easier lookup
  const allSections = useMemo(() => {
    const flattened: Record<string, SectionMetadata> = {};

    const flattenSection = (section: SectionMetadata) => {
      flattened[section.sectionKey] = section;
      section.subsections?.forEach(flattenSection);
    };

    sections.forEach(flattenSection);
    return flattened;
  }, [sections]);

  // Determine default selection - recursively find first section with content
  const defaultSelection = useMemo(() => {
    if (!sections.length) return "";

    const findFirstContentSection = (section: SectionMetadata): string => {
      // Skip custom sidebar sections (they don't have selectable content)
      if (section.isCustomSidebarSection) {
        return "";
      }
      // If this section has direct content, select it
      if (section.directContent && section.directContent.length > 0) {
        return section.sectionKey;
      }
      // Otherwise, recursively check subsections
      if (section.subsections && section.subsections.length > 0) {
        const firstSubsection = section.subsections[0];
        if (firstSubsection) return findFirstContentSection(firstSubsection);
      }
      // Fallback to this section's key
      return section.sectionKey;
    };

    // Find first non-custom section
    for (const section of sections) {
      const result = findFirstContentSection(section);
      if (result) return result;
    }

    return "";
  }, [sections]);

  const [selectedSection, setSelectedSection] = useUncontrolledState({
    state: providedSelectedSection ?? defaultSelection,
    setState: providedSetSelectedSection,
  });

  const [expanded, setExpanded] = useUncontrolledState({
    state: providedExpanded ?? false,
    setState: providedSetExpanded,
  });

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => !prev);
  }, [setExpanded]);

  const currentSection = useMemo(() => {
    return allSections[selectedSection] || sections[0];
  }, [allSections, selectedSection, sections]);

  // Build breadcrumbs based on current selection - handles multiple parent levels
  const breadcrumbs = useMemo(() => {
    const crumbs: Array<{ title: string; onClick?: () => void }> = [{ title }];

    if (!currentSection) return crumbs;

    // Recursively build parent chain
    const buildParentChain = (section: SectionMetadata): Array<{ title: string; onClick?: () => void }> => {
      const chain: Array<{ title: string; onClick?: () => void }> = [];

      if (section.parentKey) {
        const parentSection = allSections[section.parentKey];
        if (parentSection) {
          // Recursively get parent's parents
          chain.push(...buildParentChain(parentSection));
          // Add this parent with click handler
          chain.push({
            title: parentSection.props.title,
            onClick: () => setSelectedSection(section.parentKey!),
          });
        }
      }

      return chain;
    };

    // Add all parents
    crumbs.push(...buildParentChain(currentSection));

    // Add current section/subsection (no click handler for current page)
    crumbs.push({ title: currentSection.props.title });

    return crumbs;
  }, [title, currentSection, allSections, setSelectedSection]);

  // Determine which content to render
  const contentToRender = useMemo(() => {
    if (!currentSection) return null;

    // Always render only direct content (excluding nested subsections)
    return currentSection.directContent;
  }, [currentSection]);

  // Determine which top-level section is active (for sidebar highlighting)
  const activeSectionKey = useMemo(() => {
    if (!currentSection) return "";

    // Walk up the parent chain to find the top-level section
    let section = currentSection;
    while (section.parentKey) {
      const parent = allSections[section.parentKey];
      if (!parent) break;
      if (parent.depth === 0) {
        // Found top-level section
        return parent.sectionKey;
      }
      section = parent;
    }

    // If no parent or already at top level
    return currentSection.depth === 0 ? currentSection.sectionKey : "";
  }, [currentSection, allSections]);

  return (
    <ExpandableDialog
      {...expandableDialogProps}
      expanded={expanded}
      setExpanded={setExpanded}
      showDefaultExpandButton={false}
      modal={modal}
      className="min-h-[500px] p-0 overflow-hidden"
    >
      <DialogTitle className="sr-only">{title}</DialogTitle>
      {!hasSections && otherChildren}
      <SidebarProvider
        className={
          expanded ? "flex items-start w-full h-full flex-1 min-h-0" : "flex items-start flex-1 min-h-0 max-h-[500px]"
        }
      >
        <Sidebar
          collapsible="none"
          className={expanded ? "hidden md:flex flex-shrink-0 w-[240px] h-full" : "hidden md:flex w-[200px] h-full"}
        >
          <SidebarContent className="flex-1 flex flex-col">
            <SidebarGroup className="flex-1">
              <SidebarGroupContent className="flex-1">
                <SidebarMenu>
                  {sections.length ? (
                    sections.map(({ props, sectionKey, isCustomSidebarSection }) =>
                      props ? (
                        <SidebarMenuItem key={sectionKey}>
                          {isCustomSidebarSection && "customSidebarComponent" in props ? (
                            props.customSidebarComponent
                          ) : (
                            <SidebarMenuButton
                              isActive={sectionKey === activeSectionKey}
                              onClick={() => setSelectedSection(sectionKey)}
                            >
                              {"icon" in props &&
                                props.icon &&
                                (() => {
                                  const Icon = props.icon;
                                  return Icon ? <Icon /> : null;
                                })()}
                              <span>{props.title}</span>
                            </SidebarMenuButton>
                          )}
                        </SidebarMenuItem>
                      ) : null
                    )
                  ) : (
                    <SidebarMenuItem key="example">
                      <SidebarMenuButton isActive onClick={() => setSelectedSection("example")}>
                        <LucideShieldQuestion />
                        <span>Example</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>
        </Sidebar>
        <div className="flex flex-1 flex-col overflow-hidden min-h-0 min-w-0 h-full max-w-full">
          <div className="flex flex-1 flex-col gap-0 px-4 pb-4 h-full overflow-hidden min-w-0 max-w-full">
            {currentSection ? (
              <>
                <SectionHeader
                  breadcrumbs={breadcrumbs}
                  key={currentSection.sectionKey}
                  expanded={expanded}
                  toggleExpanded={toggleExpanded}
                  hideExpandToggle={hideExpandToggle}
                  trailingText={currentSection.props.trailingText}
                  actions={currentSection.props.actions}
                  headerActions={headerActions}
                />
                <div
                  className={`flex flex-1 flex-col min-w-0 max-w-full${
                    currentSection.props.scrollable ?? true ? " overflow-y-auto overflow-x-hidden" : " overflow-hidden"
                  }`}
                >
                  {contentToRender}
                </div>
              </>
            ) : (
              <>
                <SectionHeader
                  breadcrumbs={[{ title: "Example Dialog" }, { title: "Example" }]}
                  key="Example"
                  expanded={expanded}
                  toggleExpanded={toggleExpanded}
                />
                <div className="flex-1 bg-white rounded-lg shadow-sm border flex items-center justify-center h-full min-w-0 max-w-full">
                  <p className="text-gray-500 text-sm">Example</p>
                </div>
              </>
            )}
          </div>
        </div>
      </SidebarProvider>
    </ExpandableDialog>
  );
};
export const ExpandableDialogWithSidebar = Object.assign(ExpandableDialogWithSidebarComponent, {
  Section,
  Subsection,
});
ExpandableDialogWithSidebar.Section.displayName = EXPANDABLE_DIALOG_WITH_SIDEBAR_SECTION_NAME;
ExpandableDialogWithSidebar.Subsection.displayName = EXPANDABLE_DIALOG_WITH_SIDEBAR_SUBSECTION_NAME;
